import { useEffect, useRef, useState } from "react";
import { ChatMessage, ChatDataProps } from "../types/backend/SengiLabsTypes";
import { ApiPortalBackend, PortalMessage, PortalMessageContentPart } from "../apis/ApiPortalBackend";

// The chat history sidebar (ChatHistory.tsx) only ever produces real, persisted PortalBackend
// conversation ids since 2026-07-16 (Task #86/#55) - the previous "chat-<n>" fake-id convention
// is gone, so any non-empty chatID is a real conversation id now.
const isPersistedConversationId = (chatID: string) => chatID !== "";

const KNOWN_CONTENT_TYPES: ChatDataProps['type'][] = ['code', 'video', 'text', 'image', 'pdf', 'mermaid'];
const toContentType = (type: string): ChatDataProps['type'] =>
    (KNOWN_CONTENT_TYPES as string[]).includes(type) ? (type as ChatDataProps['type']) : 'text';

export const toChatMessage = (message: PortalMessage): ChatMessage => {
    // Defensiv: nur mappen, wenn contentParts wirklich ein Array ist. Liefert das Backend hier
    // einen anderen Shape (String/Objekt/null), sonst crasht ".map is not a function" und die
    // History laedt nicht mehr ("Unable to load history"). Fallback: der flache message.content.
    const contents: ChatDataProps[] = Array.isArray(message.contentParts) && message.contentParts.length > 0
        ? message.contentParts.map((part, index) => ({
            type: toContentType(part.type),
            element: part.content,
            position: index,
        }))
        : [{ type: 'text', element: message.content, position: 0 }];

    return new ChatMessage(message.id, message.role, contents);
};

const toBackendContent = (entry: ChatMessage): { content: string; contentParts: PortalMessageContentPart[] } => {
    const sortedContents = [...entry.contents].sort((a, b) => a.position - b.position);
    return {
        content: sortedContents.map(c => c.element).join('\n'),
        contentParts: sortedContents.map(c => ({ type: c.type, content: c.element })),
    };
};

//TODO: Load Chat Massages for user from database
export const useChatHistory = (chatID:string) : [chatMessages:ChatMessage[], addMessage:(entry:ChatMessage)=>void, addMessages:(entries:ChatMessage[])=>void, getLastMessage:()=>ChatMessage] => {

    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const conversationIdRef = useRef<string | null>(isPersistedConversationId(chatID) ? chatID : null);

    const persistMessage = async (entry: ChatMessage) => {
        try {
            let conversationId = conversationIdRef.current;
            if (!conversationId) {
                const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
                if (!workspaceId) return;

                const conversation = await ApiPortalBackend.CreateConversation(workspaceId, 'chat');
                if (!conversation) return;

                conversationId = conversation.id;
                conversationIdRef.current = conversationId;
            }

            const { content, contentParts } = toBackendContent(entry);
            await ApiPortalBackend.PostConversationMessage(conversationId, entry.role, content, contentParts);
        } catch (error) {
            console.warn('Failed to persist chat message:', error);
        }
    };

    function addMessage(entry:ChatMessage) {
        setChatMessages(prev => [ ...prev, entry ]);
        persistMessage(entry);
    }

    function addMessages(entries:ChatMessage[]) {
        setChatMessages(prev => [ ...prev, ...entries ]);
        entries.forEach(persistMessage);
    }

    function getLastMessage() {
        return chatMessages[chatMessages.length-1];
    }

    const loadPersistedMessages = async (conversationId: string) => {
        const messages = await ApiPortalBackend.GetConversationMessages(conversationId);
        setChatMessages(messages ? messages.map(toChatMessage) : []);
    };

    useEffect(() => {
        if (isPersistedConversationId(chatID)) {
            conversationIdRef.current = chatID;
            loadPersistedMessages(chatID);
        }
        else {
            conversationIdRef.current = null;
            setChatMessages([]);
        }
    }, [chatID]);

    return [chatMessages, addMessage, addMessages, getLastMessage];
}
